import React, { useState, useEffect, useRef } from 'react';
import { chatbotAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    // Initialize session
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // Fetch conversation history
    fetchMessages(newSessionId);
    
    // Poll every 5 seconds for updates (reduced from 2s to avoid rate limits)
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(newSessionId);
    }, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async (sid) => {
    if (!sid) return;
    try {
      const response = await chatbotAPI.getHistory(sid);
      const payload = response?.data ?? {};
      const history = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload?.history) ? payload.history : []));

      if (history.length > 0) {
        const formattedMessages = history
          .slice()
          .reverse()
          .flatMap((conv) => {
            const messagePayload = conv.messages && typeof conv.messages === 'object'
              ? conv.messages
              : { user: conv.user_message, ai: conv.ai_response, timestamp: conv.timestamp || conv.created_at };

            const userText = messagePayload?.user ?? conv.user_message ?? '';
            const aiText = messagePayload?.ai ?? conv.ai_response ?? '';
            const timestamp = messagePayload?.timestamp || conv.timestamp || conv.created_at;
            const msgs = [];

            if (userText) {
              msgs.push({
                id: `user-${conv.id}`,
                text: userText,
                sender: 'user',
                timestamp: new Date(timestamp)
              });
            }

            if (aiText) {
              msgs.push({
                id: `ai-${conv.id}`,
                text: aiText,
                sender: 'bot',
                timestamp: new Date(timestamp),
                isProcessing: false
              });
            }

            return msgs;
          });

        setMessages(formattedMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    try {
      // Save to DB - AI will be processed in background
      const response = await chatbotAPI.sendMessage(userMessage, sessionId);
      const conversationId = response.data?.conversation_id;

      // Refresh messages immediately (user message will show, AI will be null)
      if (sessionId) {
        await fetchMessages(sessionId);
      }

      // Poll for AI response using status endpoint
      if (conversationId) {
        let pollCount = 0;
        const maxPolls = 60; // 2 minutes max
        const pollForAI = setInterval(async () => {
          const statusResponse = await chatbotAPI.getStatus(conversationId);
          pollCount++;

          // Check if AI response is ready
          if (statusResponse.data?.status === 'completed') {
            // AI response is ready, update messages and stop polling
            if (sessionId) {
              await fetchMessages(sessionId);
            }
            clearInterval(pollForAI);
            setIsLoading(false);
          }

          if (pollCount >= maxPolls) {
            clearInterval(pollForAI);
            setIsLoading(false);
          }
        }, 2000);
      } else {
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
      setIsLoading(false);
    }
  };

  const quickActions = [
    { text: "Start detox program", icon: "🧹" },
    { text: "Daily detox tips", icon: "✨" },
    { text: "Withdrawal support", icon: "🤝" },
    { text: "I need motivation", icon: "💪" },
    { text: "Track my mood", icon: "😊" },
    { text: "Addiction recovery tips", icon: "🎯" },
    { text: "Exercise advice", icon: "🏃" },
    { text: "Healthy habits", icon: "🌱" }
  ];

  const handleQuickAction = (action) => {
    setInputMessage(action.text);
  };

  return (
    <div className="max-w-6xl mx-auto py-2 h-[calc(100vh-60px)] flex flex-col">
      <div className="flex-1 flex flex-col bg-white rounded-xl shadow-lg overflow-hidden min-h-0">
        {/* Welcome/Detoxify Section - Shows when no messages - fills entire chat area */}
        {messages.length === 0 && !isLoading && (
          <div className="flex-1 p-8 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
            <div className="text-center max-w-2xl">
              <div className="mb-6">
                <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-4xl">🧹</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Your Detox Journey</h2>
                <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
                  I'm here to support you through your detox and recovery process. 
                  Whether you're just starting or need ongoing support, I'm available 24/7.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Messages Area - Full Chat History */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 ${messages.length === 0 && !isLoading ? 'hidden' : ''}`}>
          {messages.map((message) => (
            <div key={message.id}>
              {message.sender === 'user' ? (
                <div className="flex justify-end">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-lg max-w-md">
                    <div className="text-sm">{message.text}</div>
                    <div className="text-xs text-blue-200 mt-1">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">AI</span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="text-gray-800 whitespace-pre-line text-sm">
                    {message.text}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-center py-4">
              <div className="flex items-center space-x-2 text-gray-500">
                <LoadingSpinner size="small" />
                <span className="text-sm">AI is thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="border-t border-gray-200 p-4 bg-white">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setInputMessage("I need motivation")}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span>💪</span>
                <span className="text-sm">Motivation</span>
              </button>
              <button
                onClick={() => setInputMessage("Help with anxiety")}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span>😰</span>
                <span className="text-sm">Anxiety</span>
              </button>
              <button
                onClick={() => setInputMessage("Sleep advice")}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span>😴</span>
                <span className="text-sm">Sleep</span>
              </button>
              <button
                onClick={() => setInputMessage("Recovery tips")}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span>🎯</span>
                <span className="text-sm">Recovery</span>
              </button>
              <button
                onClick={() => setInputMessage("Daily routine")}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <span>📅</span>
                <span className="text-sm">Routine</span>
              </button>
            </div>
          </div>

          {/* Input Form */}
          <form onSubmit={sendMessage} className="flex space-x-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask about detox, recovery, or wellness..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
